import React, { useContext, useEffect, useState } from "react";
import {
  ComboBox,
  DefaultButton,
  IComboBox,
  IComboBoxOption,
  IComboBoxStyles,
  IIconProps,
  SearchBox,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import { VisitsForPeriodQueryVariables, useLocationsQuery, useVisitsForPeriodLazyQuery } from "@graphql";
import TodaysVisitors from "../todays-visitors/TodaysVisitors";
import { isSameDay } from "date-fns";
import UpcomingsVisitors from "../upcoming-visitorsa/UpcomingsVisitors";
import { Visit } from "@graphql";
import "./VisitorsList.css";
import { UserContext } from "../../Auth";
import DisplayLocation from "../../shared/location/display-location/DisplayLocation";
import { useHistory, useParams } from "react-router-dom";
import { visitorStatus } from "../shared/enums";
import { isNil } from "lodash";
import EmptyState from "../../shared/empty-state/EmptyState";
import SignedInStatusIcon from "assets/svg/signed-in-status-icon";
import PeopleEmptyLight from "assets/svg/people-empty-light";

const VisitorsList: React.FunctionComponent = () => {
  const [getData, { data: visitorsData, loading }] = useVisitsForPeriodLazyQuery({
    fetchPolicy: "cache-and-network",
  });
  const [todaysVisitors, setTodaysVisitors] = useState<Visit[]>();
  const [upcomingsVisitors, setUpcomingsVisitors] = useState<Visit[]>();
  const [searchText, setSearchText] = useState<string>();
  const [filterStatus, setFilterStatus] = useState<string>();
  const { data: allLocations } = useLocationsQuery();

  let { idLocationParam } = useParams<{ idLocationParam: string }>();
  const [currentIdLocation, setCurrentIdLocation] = useState<string>();

  const user = useContext(UserContext);
  const history = useHistory();

  useEffect(() => {
    if (!idLocationParam) {
      idLocationParam = user.homeLocation ? user.homeLocation._id : "6225cf331fbe4d07b2f63557";
    }
    setCurrentIdLocation(idLocationParam);

    const variables: VisitsForPeriodQueryVariables = {
      locationId: idLocationParam,
      date: new Date().toDateString(),
      futureDays: 7,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      //timezone: "Europe/London",
    };
    getData({ variables: variables });
  }, []);

  useEffect(() => {
    setTodaysVisitors(getTodaysVisits());
    setUpcomingsVisitors(getUpcomingVisits());
  }, [visitorsData, searchText, filterStatus]);

  const getTodaysVisits = () => {
    const todaysVisits = visitorsData?.visitsForPeriod?.filter(
      (visit) => isSameDay(new Date(), new Date(visit.expectedDate)) && searchVisits(visit),
    );
    return todaysVisits;
  };

  const getUpcomingVisits = () => {
    const upcomingVisits = visitorsData?.visitsForPeriod?.filter(
      (visit) => !isSameDay(new Date(), new Date(visit.expectedDate)) && searchVisits(visit),
    );
    return upcomingVisits;
  };

  const searchVisits = (visit: Visit) => {
    const visitorFullName = `${visit.visitor.firstName} ${visit.visitor.lastName}`.toLocaleLowerCase();

    if (filterStatus && visit.signInStatus !== filterStatus) return false;
    if (
      !searchText ||
      searchText === "" ||
      visit.visitor.email?.toLocaleLowerCase().includes(searchText) ||
      visit.visitor.company?.toLocaleLowerCase().includes(searchText) ||
      visitorFullName.includes(searchText)
    ) {
      return true;
    }

    return false;
  };

  const statusFilterCbx: IComboBoxOption[] = [
    { key: visitorStatus.All, text: "All", selected: true },
    { key: visitorStatus.Expected, text: "Expected" },
    { key: visitorStatus.SignedIn, text: "Signed In" },
    { key: visitorStatus.SignedOut, text: "Signed Out" },
  ];

  const createVisit = () => {
    history.push(`/visitors/createVisit/${currentIdLocation}`);
  };

  const cbxBaseStyle = {
    border: "1px solid #F2F3F6",
    borderRadius: "8px",
    borderColor: "#F2F3F6",
    backgroundColor: "#F2F3F6",
    ":hover": {
      outline: "none",
      border: "1px solid #F2F3F6",
      borderRadius: "8px",
    },
    ":after": {
      outline: "none",
      border: 0,
      borderRadius: "8px",
    },
  };

  const cbxStyles: Partial<IComboBoxStyles> = {
    container: {
      ...cbxBaseStyle,
    },
    root: {
      ...cbxBaseStyle,
      height: "3rem",
    },
    input: {
      ...cbxBaseStyle,
    },
    optionsContainerWrapper: {},
  };

  const cbxOnChange = (event: React.FormEvent<IComboBox>, option: IComboBoxOption) => {
    const selectedStatus: string = option?.key?.toString();
    if (selectedStatus != visitorStatus.All) {
      setFilterStatus(selectedStatus);
    } else {
      setFilterStatus(null);
    }
  };

  const searchIconStyle: IIconProps = {
    styles: {
      root: {
        color: "#48454E",
        fontWeight: "bold",
      },
    },
  };

  const isEmptyVisitorList = (): boolean => {
    const todayVisitorsCount = todaysVisitors ? todaysVisitors.length : 0;
    const upcomingsVisitorsCount = upcomingsVisitors ? upcomingsVisitors.length : 0;
    const haveVisitors = todayVisitorsCount < 1 && upcomingsVisitorsCount < 1;
    return haveVisitors;
  };

  return (
    <>
      <div className="visitor-list-top-panel">
        <div className="display-location">
          <DisplayLocation idLocation={currentIdLocation}></DisplayLocation>
        </div>
        <hr className="dividing-line-top"></hr>
        <div className="">
          <div className="search-container">
            <SearchBox
              iconProps={searchIconStyle}
              placeholder="Search"
              className="search-box"
              onChange={(_, newValue) => setSearchText(newValue)}
            />
          </div>
          <div className="status-filter-container">
            <ComboBox styles={cbxStyles} options={statusFilterCbx} onChange={cbxOnChange}></ComboBox>
          </div>
          <hr className="dividing-line-bottom"></hr>
        </div>
      </div>
      {loading && (
        <div className="loader">
          <Spinner size={SpinnerSize.large}>Loading</Spinner>
        </div>
      )}

      {!loading && isEmptyVisitorList() && (
        <div className="empty-state">
          <EmptyState
            title={"There's nothing here"}
            description={"You don't have any upcoming visitors for today and this week."}
            svg={PeopleEmptyLight}
          ></EmptyState>
        </div>
      )}
      {!loading && !isEmptyVisitorList() && (
        <div className="list-container">
          {!loading && visitorsData && <TodaysVisitors visitors={todaysVisitors}></TodaysVisitors>}
          {!loading && visitorsData && <UpcomingsVisitors visitors={upcomingsVisitors}></UpcomingsVisitors>}
        </div>
      )}
      <div className="visitor-list-bottom-panel">
        <hr className="visitor-list-dividing-button"></hr>
        <DefaultButton toggle text="Create a Visit" onClick={() => createVisit()} className="create-visit-btn" />
      </div>
    </>
  );
};

export default VisitorsList;
